import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
    const navigate = useNavigate();
    const { user, logout } = useAdminAuth();
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    if (!user) return <AdminLoading />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setMessage("");
        if (form.new_password !== form.confirm) {
            setError("New passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API}/api/admin/settings/change-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ current_password: form.current_password, new_password: form.new_password }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not change password.");
            setMessage("Password changed. Please sign in again.");
            setTimeout(() => navigate("/admin/login", { replace: true }), 1200);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-adminSans text-[#0F172A]">
            <AdminNav active="settings" email={user.email} user={user} onLogout={logout} />
            <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-settings">
                <h1 className="font-adminDisplay text-4xl font-bold tracking-tight">Settings</h1>
                <p className="mt-2 text-sm text-[#64748B]">Manage your administrator account security.</p>

                <section className="mt-8 rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm sm:p-8">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#0B1120] text-[#D4AF37]"><KeyRound size={20} /></span>
                        <div>
                            <h2 className="font-adminDisplay text-2xl font-semibold">Change Password</h2>
                            <p className="text-sm text-[#64748B]">Changing your password signs out all admin sessions.</p>
                        </div>
                    </div>
                    <form onSubmit={submit} className="mt-7 space-y-5" data-testid="admin-change-password-form">
                        <div>
                            <label htmlFor="current-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Current password</label>
                            <input id="current-password" data-testid="current-password-input" type="password" required autoComplete="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" />
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="new-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">New password</label>
                                <input id="new-password" data-testid="new-password-input" type="password" required minLength={12} autoComplete="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" />
                            </div>
                            <div>
                                <label htmlFor="confirm-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Confirm new password</label>
                                <input id="confirm-password" data-testid="confirm-password-input" type="password" required minLength={12} autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" />
                            </div>
                        </div>
                        <p className="text-xs text-[#64748B]">Use at least 12 characters with letters and numbers.</p>
                        {error && <p data-testid="settings-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                        {message && <p data-testid="settings-success" className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</p>}
                        <button type="submit" data-testid="change-password-button" disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-[#0B1120] px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:bg-[#1E293B] disabled:opacity-60">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                            Update Password
                        </button>
                    </form>
                </section>
            </main>
        </div>
    );
}
